import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CalendarRange, Loader2, Play, Zap } from "lucide-react";
import OSLayout from "../../../components/os/OSLayout";
import { api, formatApiError } from "../../../lib/api";
import { EstadoBadge } from "../Planning";
import UnitsTab from "./UnitsTab";
import SetupTab from "./SetupTab";
import RulesTab from "./RulesTab";
import GanttTab from "./GanttTab";
import ExecutionTab from "./ExecutionTab";
import AuditTab from "./AuditTab";
import PertTab from "./PertTab";
import MapTab from "./MapTab";
import ScenariosTab from "./ScenariosTab";
import CopilotPanel from "./CopilotPanel";

const TABS = [["resumen", "Resumen"], ["unidades", "Unidades"], ["config", "Configuración"], ["reglas", "Reglas"],
  ["gantt", "Gantt"], ["pert", "PERT"], ["mapa", "Mapa"], ["escenarios", "Escenarios"], ["ejecucion", "Ejecución"], ["auditoria", "Auditoría"]];

export default function PlanDetail() {
  const { id } = useParams();
  const [plan, setPlan] = useState(null);
  const [dash, setDash] = useState(null);
  const [tab, setTab] = useState("resumen");
  const [generating, setGenerating] = useState(false);

  const reload = useCallback(() => {
    api.get(`/planning/plans/${id}`).then((r) => setPlan(r.data)).catch(() => {});
    api.get(`/planning/plans/${id}/dashboard`).then((r) => setDash(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { reload(); }, [reload]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/planning/plans/${id}/generate`);
      toast.success(`Plan generado (v${data.version}) — ${data.stats.dias_equivalentes} días equivalentes`);
      reload(); setTab("gantt");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error al generar"); }
    finally { setGenerating(false); }
  };

  const startExec = async () => {
    try { await api.post(`/planning/plans/${id}/start`); toast.success("Ejecución iniciada"); reload(); setTab("ejecucion"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };

  if (!plan) return <OSLayout title="Planning"><div className="py-24 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-navy/30" /></div></OSLayout>;

  return (
    <OSLayout title={plan.nombre}>
      <div className="space-y-5 p425" data-testid="plan-detail">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/os/planning" className="text-navy/40 hover:text-navy"><ArrowLeft className="w-5 h-5" /></Link>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-medium text-lg truncate">{plan.nombre}</h2>
              <EstadoBadge estado={plan.estado} />
              <span className="text-xs text-navy/40">v{plan.version || 0}</span>
            </div>
            <div className="text-xs text-navy/40">{plan.pais}{plan.region ? ` · ${plan.region}` : ""} · {plan.fecha_inicio} → {plan.fecha_cierre} · {plan.calendar?.total ?? "—"} días hábiles</div>
          </div>
          <div className="ml-auto flex gap-2">
            <button onClick={generate} disabled={generating} data-testid="generate-plan-btn"
              className="rounded-full bg-gradient-violet text-white text-sm px-5 py-2.5 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              {plan.version > 0 ? "Replanificar" : "Generar plan"}
            </button>
            {plan.version > 0 && plan.estado !== "en_ejecucion" && (
              <button onClick={startExec} data-testid="start-execution-btn"
                className="rounded-full bg-navy text-cream text-sm px-5 py-2.5 hover:bg-navy-soft inline-flex items-center gap-2">
                <Play className="w-4 h-4" /> Iniciar ejecución
              </button>
            )}
          </div>
        </div>

        {dash && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="plan-kpis">
            {[["Unidades", dash.total], ["Progreso", `${dash.progreso_pct}%`], ["Días restantes", dash.dias_restantes],
              ["Cerradas", dash.cerradas], ["Por auditar", dash.pendiente_auditoria], ["Correcciones", dash.correcciones_abiertas],
              ["Fin proyectado", dash.fecha_fin_proyectada ? dash.fecha_fin_proyectada.slice(5) : "—"],
              ["Confianza", dash.confianza != null ? `${dash.confianza}%` : "—"]].map(([l, v]) => (
              <div key={l} className="rounded-lg border border-navy/10 bg-white px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-wider text-navy/40">{l}</div>
                <div className="font-medium text-sm mt-0.5">{v}</div>
              </div>
            ))}
          </div>
        )}
        {dash && dash.en_tiempo === false && (
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-2.5" data-testid="risk-alert">
            ⚠ Riesgo de cierre: el cronograma proyecta {Math.abs(dash.variacion_dias)} día(s) más allá del tiempo utilizable (incluye buffer). Ajusta recursos, reglas o simula con replanificación.
          </div>
        )}

        <div className="flex gap-1 border-b border-navy/10 overflow-x-auto">
          {TABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === k ? "border-violet text-violet font-medium" : "border-transparent text-navy/50 hover:text-navy"}`}>
              {l}
            </button>
          ))}
        </div>

        {tab === "resumen" && <Overview plan={plan} dash={dash} />}
        {tab === "unidades" && <UnitsTab plan={plan} onChange={reload} />}
        {tab === "config" && <SetupTab plan={plan} onChange={reload} />}
        {tab === "reglas" && <RulesTab plan={plan} onChange={reload} />}
        {tab === "gantt" && <GanttTab plan={plan} />}
        {tab === "pert" && <PertTab plan={plan} />}
        {tab === "mapa" && <MapTab plan={plan} />}
        {tab === "escenarios" && <ScenariosTab plan={plan} onChange={reload} />}
        {tab === "ejecucion" && <ExecutionTab plan={plan} onChange={reload} />}
        {tab === "auditoria" && <AuditTab plan={plan} onChange={reload} />}
      </div>
      <CopilotPanel plan={plan} />
    </OSLayout>
  );
}

function Overview({ plan, dash }) {
  const [runs, setRuns] = useState([]);
  const [trail, setTrail] = useState([]);
  useEffect(() => {
    api.get(`/planning/plans/${plan.id}/runs`).then((r) => setRuns(r.data)).catch(() => {});
    api.get(`/planning/plans/${plan.id}/trail`).then((r) => setTrail(r.data)).catch(() => {});
  }, [plan.id]);
  const cal = plan.calendar || {};
  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="overview-tab">
      <div className="rounded-xl border border-navy/10 bg-white p-5 space-y-3">
        <div className="overline text-navy/40">Calendario automático</div>
        <p className="text-sm">{cal.total ?? 0} días hábiles entre {plan.fecha_inicio} y {plan.fecha_cierre}{plan.buffer_days > 0 ? ` · ${plan.buffer_days} día(s) de buffer reservado(s)` : ""}.</p>
        {(cal.holidays || []).length > 0 && (
          <div className="text-xs text-navy/50 space-y-1">
            <div className="font-medium text-navy/60">Feriados detectados ({plan.pais}):</div>
            {cal.holidays.slice(0, 8).map((h) => <div key={h.fecha}>{h.fecha} — {h.nombre}</div>)}
          </div>
        )}
        {(plan.calendar_exceptions || []).length > 0 && (
          <div className="text-xs text-navy/50">
            <div className="font-medium text-navy/60">Excepciones:</div>
            {plan.calendar_exceptions.map((e, i) => <div key={i}>{e.fecha} — {e.tipo === "laborable" ? "día extraordinario de trabajo" : "no laborable"}</div>)}
          </div>
        )}
      </div>
      <div className="rounded-xl border border-navy/10 bg-white p-5 space-y-2">
        <div className="overline text-navy/40">Versiones del plan</div>
        {runs.length === 0 ? <p className="text-sm text-navy/40">Aún no se genera ninguna versión. Usa "Generar plan".</p> :
          runs.map((r) => (
            <div key={r.id} className="text-sm flex items-center gap-2">
              <span className="font-medium">v{r.version}</span>
              <span className="text-navy/40 text-xs">{(r.created_at || "").slice(0, 16).replace("T", " ")} · {r.stats?.dias_equivalentes}d · {r.stats?.en_tiempo ? "en tiempo" : "en riesgo"}</span>
            </div>
          ))}
      </div>
      <div className="rounded-xl border border-navy/10 bg-white p-5 space-y-2">
        <div className="overline text-navy/40">Audit trail</div>
        {trail.slice(0, 10).map((t) => (
          <div key={t.id} className="text-xs text-navy/50">
            <span className="text-navy/70 font-medium">{t.accion.replaceAll("_", " ")}</span> · {t.usuario} · {(t.created_at || "").slice(0, 16).replace("T", " ")}
          </div>
        ))}
        {trail.length === 0 && <p className="text-sm text-navy/40">Sin movimientos aún.</p>}
      </div>
    </div>
  );
}
